import KuikaSkeleton from "../../../../ui-kit/kuika-skeleton/kuika-skeleton";

/**
 * Placeholder for the Elements catalogue while the project's component set is still being
 * fetched (`ComponentSetService.Get` / `ComponentService.GetAllByComponentSetId`, kicked off
 * inside `loadProject`). Until then the panel's chrome — tab switcher and search header —
 * paints, and the scroll area below it is simply empty, so the whole shell reads as a hung
 * frame behind the loading overlay.
 *
 * The composition mirrors the real catalogue's geometry exactly (36px category header, a
 * three-column grid of 80px cards, 40x40 icon over a label line), so the skeleton hands over
 * to real cards without the list shifting. It deliberately shows FEWER cards than the real
 * catalogue's 40-plus: enough to establish the shape and to read as "more below", without
 * claiming a specific count that then changes under the user.
 */

/**
 * Card counts per section. Measured against the real panel at the default 238px width: the
 * scroll area is ~709px, a card row is 80px and a header 36px, so 9 + 12 cards under two
 * headers fills it (3 + 4 rows = 560px + 72px) with the last row cut by the fold — which is
 * what says "more below". The first draft's 9 + 3 stopped 299px short and read as a list that
 * had finished loading with barely anything in it.
 */
const SKELETON_SECTIONS: number[] = [9, 12];

/** Varied so the block reads as a list of names rather than a printed pattern. */
const LABEL_WIDTHS: string[] = ["72%", "58%", "80%", "64%", "76%", "54%"];

const ComponentToolKitSkeleton = (): JSX.Element => (
  <div className="kuika-component-panel__skeleton" aria-hidden="true" data-testid="componentToolKitSkeleton">
    {SKELETON_SECTIONS.map((cardCount: number, sectionIndex: number) => (
      <div key={sectionIndex} className="kuika-component-panel__skeleton-section">
        <div className="kuika-component-panel__skeleton-header">
          <KuikaSkeleton width={sectionIndex === 0 ? 104 : 78} height={12} />
        </div>
        <div className="kuika-component-panel__skeleton-grid">
          {Array.from({ length: cardCount }, (_unused: unknown, cardIndex: number) => (
            <div key={cardIndex} className="kuika-component-panel__skeleton-card">
              <KuikaSkeleton className="kuika-component-panel__skeleton-icon" width={40} height={40} />
              <KuikaSkeleton width={LABEL_WIDTHS[(sectionIndex * 3 + cardIndex) % LABEL_WIDTHS.length]} height={11} />
            </div>
          ))}
        </div>
      </div>
    ))}
  </div>
);

export default ComponentToolKitSkeleton;
